import { useEffect, useLayoutEffect, useState } from 'react'
import { allBlocksValue, allFacilitiesValue, allStaffValue, unassignedStaffValue } from 'Pages/Attendance/attendanceDashboardFormatters'
import { type AttendanceAccountContext, type AttendanceDashboardDto } from 'Pages/Attendance/types'
import { sendGet } from 'hooks/use-fetch'

export interface AttendanceDashboardState {
  data?: AttendanceDashboardDto
  error: string[]
  loading: boolean
}

export interface AttendanceDashboardFilters {
  blockFilter: string
  facilityFilter: string
  staffFilter: string
}

export interface AttendanceDashboardRouteFilters {
  providerId?: number | string
  facilityId?: number | string
  from?: string
  to?: string
  providerSearch?: string
}

const dashboardCache = new Map<string, AttendanceDashboardDto>()

export function clearAttendanceDashboardCache (): void {
  dashboardCache.clear()
}

export const defaultAttendanceDashboardFilters: AttendanceDashboardFilters = {
  blockFilter: allBlocksValue,
  facilityFilter: allFacilitiesValue,
  staffFilter: allStaffValue
}

export function useAttendanceDashboard (
  accountContext: AttendanceAccountContext,
  filters: AttendanceDashboardFilters = defaultAttendanceDashboardFilters,
  routeFilters: AttendanceDashboardRouteFilters = {}
): AttendanceDashboardState {
  const dashboardRequestUrl = dashboardUrl(filters, routeFilters)
  const dashboardRequestKey = accountContext.ready
    ? [
        accountContext.accountType,
        accountContext.accountId,
        accountContext.currentISDID,
        dashboardRequestUrl
      ].join(':')
    : undefined
  const [historyRestoreCount, setHistoryRestoreCount] = useState(0)
  const [dashboardState, setDashboardState] = useState<AttendanceDashboardState>(() => ({
    data: dashboardRequestKey === undefined ? undefined : dashboardCache.get(dashboardRequestKey),
    error: [],
    loading: accountContext.ready
  }))

  useLayoutEffect(() => {
    if (!accountContext.ready) {
      setDashboardState({ error: [], loading: false })
      return
    }

    setDashboardState({
      data: dashboardRequestKey === undefined ? undefined : dashboardCache.get(dashboardRequestKey),
      error: [],
      loading: true
    })
  }, [accountContext.ready, dashboardRequestKey])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) {
        return
      }

      setDashboardState(current => ({
        ...current,
        data: dashboardRequestKey === undefined ? current.data : dashboardCache.get(dashboardRequestKey) ?? current.data,
        error: [],
        loading: accountContext.ready
      }))
      setHistoryRestoreCount(count => count + 1)
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [accountContext.ready, dashboardRequestKey])

  useEffect(() => {
    if (!accountContext.ready || dashboardRequestKey === undefined) {
      setDashboardState({ error: [], loading: false })
      return
    }

    const controller = new AbortController()
    let cancelled = false

    const loadDashboard = async (): Promise<void> => {
      setDashboardState(current => ({ ...current, error: [], loading: true }))
      const { response, success, error } = await sendGet(dashboardRequestUrl, {}, controller)

      if (cancelled) {
        return
      }

      if (!success) {
        setDashboardState({
          error: error.filter(message => message.trim() !== '').length === 0 ? ['Attendance dashboard could not be loaded.'] : error,
          loading: false
        })
        return
      }

      const dashboard = response as AttendanceDashboardDto
      dashboardCache.set(dashboardRequestKey, dashboard)
      setDashboardState({ data: dashboard, error: [], loading: false })
    }

    void loadDashboard()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [accountContext.ready, dashboardRequestKey, dashboardRequestUrl, historyRestoreCount])

  return dashboardState
}

function dashboardUrl (filters: AttendanceDashboardFilters, routeFilters: AttendanceDashboardRouteFilters): string {
  const query = new URLSearchParams()

  if (routeFilters.providerId !== undefined) {
    query.set('providerId', String(routeFilters.providerId))
  }

  if (routeFilters.facilityId !== undefined) {
    query.set('facilityId', String(routeFilters.facilityId))
  }

  if (routeFilters.from !== undefined) {
    query.set('from', routeFilters.from)
  }

  if (routeFilters.to !== undefined) {
    query.set('to', routeFilters.to)
  }

  if (routeFilters.providerSearch !== undefined && routeFilters.providerSearch.trim() !== '') {
    query.set('providerSearch', routeFilters.providerSearch.trim())
  }

  if (filters.blockFilter !== allBlocksValue) {
    query.set('sessionBlockId', filters.blockFilter)
  }

  if (filters.facilityFilter !== allFacilitiesValue && routeFilters.facilityId === undefined) {
    query.set('facilityId', filters.facilityFilter)
  }

  if (filters.staffFilter !== allStaffValue) {
    if (filters.staffFilter === unassignedStaffValue) {
      query.set('unassignedStaffOnly', 'true')
    } else {
      query.set('staffMemberId', filters.staffFilter)
    }
  }

  const queryString = query.toString()
  return queryString.length === 0 ? '/Attendance/Dashboard' : `/Attendance/Dashboard?${queryString}`
}
